import { useState } from "react";
import { Trash2 } from "lucide-react";
import type { BudgetOwner, DashboardData } from "../../types";
import { OVERALL_CATEGORY } from "../../lib/budgets";
import { api } from "../../lib/api";
import FormActions from "../../components/FormActions";

export interface BudgetFormProps {
  data: DashboardData;
  category?: string;
  owner?: BudgetOwner;
  allowPickTarget?: boolean;
  addableOwners?: BudgetOwner[];
  month: string;
  existing?: { amount: number | null; repeats: boolean; currency: string };
  onDone: () => void;
  onCancel?: () => void;
}

export default function BudgetForm({
  data,
  category = OVERALL_CATEGORY,
  owner = "Household",
  allowPickTarget = false,
  addableOwners,
  month,
  existing,
  onDone,
}: BudgetFormProps) {
  const [targetCategory, setTargetCategory] = useState(category);
  const [targetOwner, setTargetOwner] = useState<BudgetOwner>(owner);
  const [amount, setAmount] = useState(existing?.amount != null ? String(existing.amount) : "");
  const [repeats, setRepeats] = useState(existing?.repeats ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(rawAmount: string) {
    setBusy(true);
    setError("");
    try {
      await api("budgets/save", {
        owner: allowPickTarget ? targetOwner : owner,
        category: allowPickTarget ? targetCategory : category,
        month,
        amount: rawAmount.trim().replace(/,/g, ""),
        currency: data.baseCurrency,
        repeats,
      });
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const effectiveCategory = allowPickTarget ? targetCategory : category;
  const isEditingExisting = existing && existing.amount !== null;

  return (
    <form
      className="budget-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit(amount);
      }}
    >
      {allowPickTarget && (
        <>
          <label>
            Category
            <select
              value={targetCategory}
              onChange={(e) => setTargetCategory(e.target.value)}
            >
              <option value={OVERALL_CATEGORY}>Overall spending</option>
              {(data.entryOptions?.categories || []).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label>
            Who
            <select
              value={targetOwner}
              onChange={(e) => setTargetOwner(e.target.value as BudgetOwner)}
            >
              {(addableOwners || [owner]).map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
      <label>
        Amount ({data.baseCurrency})
        <input
          autoFocus={!allowPickTarget}
          inputMode="decimal"
          required
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
        />
        <small>{isEditingExisting ? "Clear to stop budgeting this." : "Maximum planned spend for this category."}</small>
      </label>
      <label className="budget-form-repeats">
        <input
          type="checkbox"
          checked={repeats}
          onChange={(e) => setRepeats(e.target.checked)}
        />
        Apply to future months too
      </label>
      {error && <p className="form-error">{error}</p>}
      <FormActions
        label="Save budget"
        busy={busy}
        disabled={!effectiveCategory || !amount.trim()}
        secondary={
          isEditingExisting ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void submit("")}
            >
              <Trash2 size={14} /> Delete budget
            </button>
          ) : undefined
        }
      />
    </form>
  );
}
